import moment from 'moment';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import mammoth from 'mammoth';
import Tesseract from 'tesseract.js';
import { franc } from 'franc-min';
import { v4 as uuidv4 } from 'uuid';
import i18n from 'i18next';
import { BREAK_LINE_DELIMITER } from '@src/constants';
import {
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  TEXT_FILE_FORMAT,
} from '@src/constants/voice';
import {
  ALOWED_CHARACTERS_PDF_REGEX,
  FRANC_LANG_VN,
  MEGA_BYTE,
  PDF_SCAN_ERROR_THRESHOLD,
  TYPE_UPLOAD,
} from '@src/constants/upload';
import {
  DUB_FILE_FORMAT,
  DUBBING_LENGTH_LIMIT,
  MAX_SENTENCE_LENGTH,
} from '@src/constants/dubbing';
import { MAX_CHARACTER_LENGTH } from '@src/constants/tts';
import {
  NOTE_MAX_LENGTH,
  PROJECT_PRODUCT,
  PROJECT_TYPE,
} from '@src/constants/project';
import {
  MAX_AUDIO_DURATION_SECONDS,
  MAX_AUDIO_SIZE_BYTES,
  SUBTITLE_MAX_TOTAL_CHARS,
  TRANSCRIPT_FILE_FORMAT,
  TRANSCRIPT_S3_DIRECTORY,
  VIDEO_DUBBING_FILE_FORMAT,
  VIDEO_DUBBING_MAX_DURATION_SECONDS,
} from '@src/constants/transcript';
import {
  PRESENTATION_FILE_FORMAT,
  PRESENTATION_S3_DIRECTORY,
} from '@src/constants/presentation';
import { countTextLength, removeEmptyLineOfText } from '@src/services/tts';
import { MOE_PROJECT_SOURCE_TYPE } from '@src/constants/moengage';
import { validateSrtProject } from '@src/services/dubbing';
import { getAudioDuration, formatDuration } from '@src/services/audio';
import { uploadToS3FromFile } from '@src/services/upload';
import { registerLocalSource } from '@src/services/mediaExtractor';
import { startTimelinePrewarm } from '@src/services/timelinePrewarm';
import {
  convertSecondsToHMS,
  convertSecondsToSubtitleTime,
} from '@src/utils/time';
import { parseSRTFileProject } from '@src/utils/srt';
import { validateSRTDuration } from '@vbee-holding/aivoice-shared-lib';
import {
  getSlideCountFromPptx,
  getNoteCountFromPptx,
  getOverlimitNotesSlides,
} from '@src/utils/presentation';
import {
  formatFileSize,
  readFileAsText,
  readFileAsArrayBuffer,
} from '@src/utils/file';
import pluralize from '@src/utils/pluralize';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

const ALLOWED_TEXT_EXTENSIONS = [
  ...TEXT_FILE_FORMAT,
  'pdf',
  ...DUB_FILE_FORMAT,
];

const getExt = (name) => name?.split('.').pop()?.toLowerCase();

// ---------- Default title ----------

export const buildDefaultTitle = (t) =>
  t('untitledTitleProject', { time: moment().format('YYYYMMDD-HH:mm') });

// ---------- Text extraction (txt / docx / pdf + OCR fallback) ----------

const replaceMultipleSpacesWithNewline = (input) =>
  input.replace(/ {3,}/g, '\n');

const replaceDoubleSpaces = (str) => str.replace(/ {2,}/g, ' ');

const checkStrMoreThanTwoSpaces = (str) => str.split(' ').length > 2;

const isErrorCharacter = (char) => {
  if (char === '\n' || char === '\r') return false;
  return !ALOWED_CHARACTERS_PDF_REGEX.test(char);
};

const checkPdfIsScanned = (text) => {
  if (!text?.trim()) return true;
  const errorCount = [...text].filter(isErrorCharacter).length;
  const errorRate = (errorCount / text.length) * 100;
  return errorRate > PDF_SCAN_ERROR_THRESHOLD;
};

const getTextContentPdf = async (pages) => {
  const textContents = await Promise.all(
    pages.map((page) => page.getTextContent()),
  );

  const strMoreThanTwoSpaces = textContents[0].items.some((item) =>
    checkStrMoreThanTwoSpaces(item.str),
  );

  const text = textContents
    .flatMap((tc) => tc.items)
    .map((item) => item.str)
    .join(strMoreThanTwoSpaces ? '' : ' ');

  const first100 = text.substring(0, 100);
  const language = franc(first100);
  if (language === FRANC_LANG_VN) {
    return replaceDoubleSpaces(replaceMultipleSpacesWithNewline(text));
  }
  return text;
};

const getTextContentPdfScanned = async (pages) => {
  const results = await Promise.all(
    pages.map(async (page) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const viewport = page.getViewport({ scale: 2 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
      const dataUrl = canvas.toDataURL();
      return Tesseract.recognize(dataUrl, FRANC_LANG_VN);
    }),
  );
  return results.map((r) => r.data.text).join(' ');
};

export const extractTxt = async (file) => (await readFileAsText(file)) || '';

export const extractDocx = async (file) => {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const { value } = await mammoth.extractRawText({ arrayBuffer });
  return value || '';
};

export const extractPdf = async (file) => {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
  const numPages = await pdf.numPages;
  const pages = await Promise.all(
    Array.from({ length: numPages }, (_, i) => pdf.getPage(i + 1)),
  );
  let text = await getTextContentPdf(pages);
  if (checkPdfIsScanned(text)) {
    text = await getTextContentPdfScanned(pages);
  }
  return text || '';
};

export const buildEmptyDubbingBlocks = () => [
  {
    id: uuidv4(),
    speed: 1,
    text: '',
    startTime: convertSecondsToSubtitleTime(0),
    endTime: convertSecondsToSubtitleTime(2),
  },
];

// A brand new TTS project opens on a single empty paragraph so the editor has
// somewhere to type. Mirrors buildEmptyDubbingBlocks for the TTS product.
export const buildEmptyTtsBlocks = () => [
  {
    id: uuidv4(),
    text: '',
    characters: 0,
    speed: DEFAULT_SPEED,
    elements: [],
  },
];

export const buildTtsBlocksFromText = (text) => {
  const handled = removeEmptyLineOfText(text || '');
  const paragraphs = handled.split(BREAK_LINE_DELIMITER);
  return paragraphs.map((p) => ({
    id: uuidv4(),
    text: p || '',
    breakTime: DEFAULT_BREAK_TIME,
    speed: DEFAULT_SPEED,
    characters: countTextLength(p),
    elements: [
      {
        key: uuidv4(),
        text: p,
        startOffset: 0,
        endOffset: (p || '').length,
        name: undefined,
        value: undefined,
      },
    ],
  }));
};

export const parseTextFile = async (file) => {
  const ext = getExt(file.name);
  let text = '';
  switch (ext) {
    case 'txt':
      text = await extractTxt(file);
      break;
    case 'docx':
      text = await extractDocx(file);
      break;
    case 'pdf':
      text = await extractPdf(file);
      break;
    default:
      throw new Error('unsupported');
  }

  const first100 = (text || '').slice(0, 100);
  const lang = franc(first100);
  let finalText = text;
  if (lang === FRANC_LANG_VN) {
    finalText = finalText.replace(/ {2,}/g, '\n').replace(/\n{2,}/g, '\n');
  }

  const sourceTypeByExt = {
    txt: MOE_PROJECT_SOURCE_TYPE.FILE_TXT,
    docx: MOE_PROJECT_SOURCE_TYPE.FILE_DOC,
    pdf: MOE_PROJECT_SOURCE_TYPE.FILE_PDF,
  };

  return {
    product: PROJECT_PRODUCT.TTS,
    blocks: buildTtsBlocksFromText(finalText),
    sourceType: sourceTypeByExt[ext],
  };
};

// ---------- SRT → DUBBING payload ----------

const buildSentencesMap = (parsedSubtitles) =>
  parsedSubtitles.reduce((acc, s) => {
    const id = uuidv4();
    acc[id] = {
      id,
      start: s.start,
      end: s.end,
      content: s.content || '',
    };
    return acc;
  }, {});

const toDubbingBlocks = (sentencesMap) =>
  Object.keys(sentencesMap).map((id) => {
    const s = sentencesMap[id];
    return {
      id: uuidv4(),
      speed: 1,
      text: s.content || '',
      startTime: s.start,
      endTime: s.end,
    };
  });

/**
 * Route an uploaded file to the right product payload.
 * `.srt` → DUBBING (uses `validateSrtProject` from services/dubbing).
 * Other text formats → TTS via `parseTextFile`.
 */
export const processFileVariant = async (file) => {
  const ext = getExt(file.name);

  if (ext === 'srt') {
    const result = await validateSrtProject(file);
    if (result?.error) {
      const err = new Error(result.message || 'invalidFormat');
      err.detail = result.detail;
      throw err;
    }
    const sentences = buildSentencesMap(result.subtitles || []);
    return {
      product: PROJECT_PRODUCT.DUBBING,
      blocks: toDubbingBlocks(sentences),
      language: i18n.language,
      extra: { sentences, totalSeconds: result.totalSeconds || 0 },
    };
  }

  return parseTextFile(file);
};

// ---------- Validators ----------

/**
 * Validate a text-like project file (.txt / .pdf / .docx / .srt).
 * Returns null when valid; a localized error message otherwise.
 *
 * Note: legacy enforces file-size limits at the dropzone level (`maxSize` prop
 * with `MEGA_BYTE`), not in the service. This validator only checks extension
 * and SRT content.
 */
/**
 * Returns null when valid; `{ message, detail? }` when invalid. `detail` is
 * raw HTML (line numbers / timestamps) used by SRT validation to point at the
 * specific failing block — the popup must render it via `dangerouslySetInnerHTML`.
 *
 * Extension is already enforced by the popup against `config.fileType`, so
 * this validator skips a duplicate check and only inspects content (SRT only).
 */
export const validateFileVariant = async (file) => {
  const ext = getExt(file?.name || '');
  if (ext === 'srt') {
    const res = await validateSrtProject(file);
    if (res?.error) {
      // SRT error keys reference dynamic limits — must pass the same
      // interpolation context as legacy `Dubbing/UploadSubtitleFile`
      // (e.g. `audioLengthTooLong` needs `maxAudioLength`).
      const interpolation = {
        maxAudioLength: DUBBING_LENGTH_LIMIT,
        fileType: DUB_FILE_FORMAT.map((type) => `.${type}`).join(','),
        maxSentenceLength: MAX_SENTENCE_LENGTH,
        maxCharacterLength: MAX_CHARACTER_LENGTH,
      };
      return {
        message: i18n.t(res.message || 'invalidFormat', interpolation),
        detail: res.detail,
      };
    }
  }
  return null;
};

/**
 * Validate a PPTX file by attempting to read its slide count.
 * Returns null when valid; localized error otherwise.
 *
 * Note: legacy `UploadFileByPptx.jsx` does not enforce a file-size cap, so
 * neither does this validator. Size limits should be applied at the dropzone
 * level via the `maxSize` prop if needed.
 */
export const validatePptxFile = async (file) => {
  if (!file) return i18n.t('invalidFormat');
  try {
    const count = await getSlideCountFromPptx(file);
    if (!count) return i18n.t('invalidFormat');
    const overlimitSlides = await getOverlimitNotesSlides(
      file,
      NOTE_MAX_LENGTH,
    );
    if (overlimitSlides.length > 0) {
      return i18n.t('notesExceedLimit', {
        slides: overlimitSlides.join(', '),
        max: NOTE_MAX_LENGTH.toLocaleString(i18n.language),
      });
    }
    return null;
  } catch {
    return i18n.t('invalidFormat');
  }
};

/**
 * Validate an audio/video file for transcription. Returns null when valid;
 * localized error otherwise.
 *
 * Mirrors `UploadFileByAudio/index.jsx` `handleFileChange`: checks duration +
 * empty audio. File-size limits are enforced at the dropzone level via
 * `maxSize={MAX_AUDIO_SIZE_BYTES}`.
 */
export const validateAudioFile = async (file) => {
  if (!file) return i18n.t('invalidFormat');
  try {
    const seconds = await getAudioDuration(file);
    if (!seconds) return i18n.t('invalidNoAudio');
    if (seconds > MAX_AUDIO_DURATION_SECONDS) {
      return i18n.t('audioDurationExceeded', {
        max: formatDuration(MAX_AUDIO_DURATION_SECONDS),
      });
    }
    return null;
  } catch {
    return i18n.t('invalidNoAudio');
  }
};

const computeDetailsPptx = async (file, t) => {
  const [slides, notes] = await Promise.all([
    getSlideCountFromPptx(file).catch(() => 0),
    getNoteCountFromPptx(file).catch(() => 0),
  ]);
  return [
    t('slideCount', { count: slides }),
    t('noteCount', { count: notes }),
    formatFileSize(file.size),
  ];
};
const processPptx = async (file) => {
  const url = await uploadToS3FromFile(file, PRESENTATION_S3_DIRECTORY);
  if (!url) throw new Error('upload-failed');
  return {
    product: PROJECT_PRODUCT.PRESENTATION,
    blocks: [],
    source: {
      url,
      type: PROJECT_TYPE.PRESENTATION,
    },
  };
};

const computeDetailsAudio = async (file) => {
  const seconds = await getAudioDuration(file).catch(() => 0);
  return [convertSecondsToHMS(seconds), formatFileSize(file.size)];
};

/** Total trimmed character count across parsed .srt subtitle blocks. */
export const sumSubtitleChars = (subtitles = []) =>
  subtitles.reduce((sum, s) => sum + (s?.content?.trim()?.length || 0), 0);

const computeDetailsFile = async (file, t) => {
  const ext = getExt(file?.name || '');
  if (ext !== 'srt') return undefined;

  const result = await validateSrtProject(file).catch(() => null);
  if (!result || result.error) return undefined;

  const { subtitles = [], totalSeconds = 0 } = result;
  const totalChars = sumSubtitleChars(subtitles);

  return [
    `${totalChars.toLocaleString(i18n.language)} ${pluralize(
      totalChars,
      t('characterV2'),
      i18n.language,
    )}`,
    convertSecondsToHMS(totalSeconds),
    formatFileSize(file.size),
  ];
};

const processAudio = async (file) => {
  const ext = getExt(file.name);
  const isVideo =
    VIDEO_DUBBING_FILE_FORMAT.includes(ext) || file.type?.startsWith('video/');
  // The upload and the filmstrip read the same local bytes, so decode the strip
  // WHILE the file uploads; once the url (the cache key) exists, the frames
  // land in the strip cache the timeline reads first.
  const prewarm = isVideo ? startTimelinePrewarm(file) : null;
  let url;
  try {
    url = await uploadToS3FromFile(file, TRANSCRIPT_S3_DIRECTORY);
    if (!url) throw new Error('upload-failed');
  } catch (error) {
    // No url means no project and no cache key — the head start is worthless.
    prewarm?.cancel();
    throw error;
  }
  // The timeline can read this source straight from the file that was just
  // uploaded, instead of pulling the same bytes back down from S3.
  registerLocalSource(url, file);
  prewarm?.attachUrl(url);
  const sourceType = isVideo ? PROJECT_TYPE.VIDEO : PROJECT_TYPE.AUDIO;
  const duration = await getAudioDuration(file).catch(() => 0);
  return {
    product: PROJECT_PRODUCT.DUBBING,
    blocks: [],
    source: {
      url,
      type: sourceType,
      metadata: {
        duration,
        size: file.size,
      },
    },
  };
};

/**
 * Subtitle-creation method for the video dubbing project popup (AIV-12773 US-03).
 *
 * The legacy "auto-detect voice" control is a boolean switch. The new UI (gated
 * by `VIDEO_DUBBING_SRT_UPLOAD`) replaces it with a method dropdown:
 *   - AUTO / NONE map 1:1 to the existing `aiConfig.enabled` boolean, so every
 *     downstream credit/guard/submit branch keeps working unchanged.
 *   - SRT uploads an existing subtitle file — no STT, blocks come from the .srt
 *     while the video is still stored as the project source.
 */
export const SUBTITLE_MODE = {
  AUTO: 'auto',
  SRT: 'srt',
  NONE: 'none',
};

/** Whether the chosen method auto-generates subtitles (drives `aiConfig.enabled`). */
export const isAutoSubtitleMode = (mode) => mode === SUBTITLE_MODE.AUTO;

/** Derive the dropdown selection from the `aiConfig.enabled` boolean. */
export const getSubtitleModeFromEnabled = (enabled) =>
  enabled ? SUBTITLE_MODE.AUTO : SUBTITLE_MODE.NONE;

/**
 * Validate a .srt uploaded via the video dubbing subtitle-method dropdown.
 * Reuses the shared SRT validator (format / block / word limits) and adds the
 * total-character cap specific to this flow. Returns null when valid, otherwise
 * `{ message, detail? }` (same shape the dropzone renders).
 */
export const validateVideoSubtitleSrt = async (file) => {
  // Enforce the 90-minute video project limit (BR_7) on the .srt timeline FIRST.
  // This must run before validateFileVariant, whose shared validateSrtProject
  // surfaces `audioLengthTooLong` at the legacy 4h cap — running the 90-minute
  // check first means an over-length SRT always reports the 90-minute limit
  // here and never the 4h message. Reuses the shared lib's validateSRTDuration
  // with an explicit 90-minute max (its default is the 4h dubbing cap), so the
  // generic SRT-upload flow stays at 4h while the video flow is stricter.
  // Parsed independently so files exceeding even the 4h cap are still caught.
  const { parsedSubtitles } = await parseSRTFileProject(file).catch(() => ({
    parsedSubtitles: [],
  }));
  const subtitlesForValidation = parsedSubtitles.map(({ start, end }) => ({
    startTime: start,
    endTime: end,
  }));
  const { isValid } =
    validateSRTDuration(
      subtitlesForValidation,
      VIDEO_DUBBING_MAX_DURATION_SECONDS * 1000,
    ) || {};
  if (!isValid) {
    return {
      message: i18n.t('videoSubtitleDurationExceeded', {
        max: formatDuration(VIDEO_DUBBING_MAX_DURATION_SECONDS),
      }),
    };
  }

  const base = await validateFileVariant(file);
  if (base) return base;

  const result = await validateSrtProject(file).catch(() => null);
  if (result && !result.error) {
    const totalChars = sumSubtitleChars(result.subtitles);
    if (totalChars > SUBTITLE_MAX_TOTAL_CHARS) {
      return {
        message: i18n.t('subtitleCharLimitExceeded', {
          max: SUBTITLE_MAX_TOTAL_CHARS.toLocaleString(i18n.language),
        }),
      };
    }
  }
  return null;
};

/**
 * Merge a processed video payload (source) with an SRT-derived payload (blocks)
 * for the "upload existing subtitle" mode: the video stays the project source
 * while blocks/timings come from the .srt. No language/model hints are added —
 * this path performs no STT. `autoGenerateSubtitles` is forced to `false` so the
 * backend does not re-generate subtitles over the ones the user uploaded.
 */
export const buildVideoSrtCreatePayload = ({ videoPayload, srtPayload }) => ({
  product: PROJECT_PRODUCT.DUBBING,
  source: {
    ...videoPayload.source,
    autoGenerateSubtitles: false,
  },
  blocks: srtPayload.blocks,
  extra: srtPayload.extra,
  language: srtPayload.language,
});

// ---------- FILE_VARIANTS config ----------

export const FILE_VARIANTS = {
  [TYPE_UPLOAD.FILE]: {
    titleKey: 'uploadYourFile',
    fileType: ALLOWED_TEXT_EXTENSIONS,
    maxSize: MEGA_BYTE,
    validate: validateFileVariant,
    computeDetails: computeDetailsFile,
    process: processFileVariant,
    withAI: false,
  },

  [TYPE_UPLOAD.PPTX]: {
    titleKey: 'dubbedSlider',
    fileType: PRESENTATION_FILE_FORMAT,
    maxSize: MEGA_BYTE,
    validate: validatePptxFile,
    computeDetails: computeDetailsPptx,
    process: processPptx,
    withAI: true,
    // Models loaded at runtime via the `PRESENTATION_AI_MODELS` feature flag;
    // toggle gated by `PRESENTATION_LLM`.
    modelsSource: 'pptx',
  },

  [TYPE_UPLOAD.AUDIO]: {
    titleKey: 'createSrtProject',
    fileType: TRANSCRIPT_FILE_FORMAT,
    maxSize: MAX_AUDIO_SIZE_BYTES,
    validate: validateAudioFile,
    computeDetails: computeDetailsAudio,
    process: processAudio,
    withAI: true,
    // Models loaded at runtime via `TRANSCRIPT_AUDIO_MODELS` feature flag.
    modelsSource: 'audio',
  },

  [TYPE_UPLOAD.VIDEO_DUBBING]: {
    titleKey: 'dubbedVideo',
    fileType: VIDEO_DUBBING_FILE_FORMAT,
    maxSize: MAX_AUDIO_SIZE_BYTES,
    validate: validateAudioFile,
    computeDetails: computeDetailsAudio,
    process: processAudio,
    withAI: true,
    // Reuses transcript audio models — same backend pipeline, video-only inputs.
    modelsSource: 'audio',
  },
};
